import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Eye, EyeOff } from "lucide-react";
import logoLoginCadastro from "@/assets/logo-login-cadastro.png";
import "@/styles/auth-animations.css";

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetLoading, setResetLoading] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        // Verificar se o erro é de email não confirmado
        if (error.message.includes("Email not confirmed") || error.message.includes("email_not_confirmed")) {
          toast.error("Por favor, confirme seu email antes de fazer login. Verifique sua caixa de entrada.");
          navigate(`/confirmar-email?email=${encodeURIComponent(email)}`);
          return;
        }
        throw error;
      }

      if (data.user) {
        // Verificar se o email foi confirmado
        if (!data.user.email_confirmed_at) {
          toast.error("Por favor, confirme seu email antes de fazer login.");
          navigate(`/confirmar-email?email=${encodeURIComponent(email)}`);
          return;
        }

        toast.success("Login realizado com sucesso!");
        navigate("/");
      }
    } catch (error: any) {
      // Traduzir mensagens de erro do Supabase para português amigável
      let mensagemErro = "Erro ao fazer login";

      if (error?.message) {
        const msg = error.message.toLowerCase();
        
        if (msg.includes("invalid login credentials") || msg.includes("invalid email or password")) {
          mensagemErro = "❌ Email ou senha incorretos. Tente novamente.";
        } else if (msg.includes("email not confirmed")) {
          mensagemErro = "📧 Por favor, confirme seu email antes de fazer login.";
        } else if (msg.includes("too many requests")) {
          mensagemErro = "⏱️ Muitas tentativas de login. Tente novamente em alguns minutos.";
        } else if (msg.includes("user not found")) {
          mensagemErro = "👤 Usuário não encontrado. Verifique o email ou crie uma nova conta.";
        } else if (msg.includes("account disabled")) {
          mensagemErro = "🔒 Sua conta foi desativada. Entre em contato com o suporte.";
        } else if (msg.includes("network") || msg.includes("timeout")) {
          mensagemErro = "🌐 Erro de conexão. Verifique sua internet e tente novamente.";
        } else {
          // Manter mensagem original se não for um erro conhecido
          mensagemErro = error.message || "Erro ao fazer login";
        }
      }

      toast.error(mensagemErro);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;

      toast.success("✅ Email de recuperação enviado! Verifique sua caixa de entrada e spam.");
      setShowForgotPassword(false);
      setResetEmail("");
    } catch (error: any) {
      // Traduzir mensagens de erro
      let mensagemErro = "Erro ao enviar email de recuperação";

      if (error?.message) {
        const msg = error.message.toLowerCase();
        
        if (msg.includes("user not found")) {
          mensagemErro = "👤 Email não encontrado. Verifique se está correto ou crie uma nova conta.";
        } else if (msg.includes("too many requests")) {
          mensagemErro = "⏱️ Muitas solicitações. Tente novamente em alguns minutos.";
        } else if (msg.includes("email not confirmed")) {
          mensagemErro = "📧 Por favor, confirme seu email primeiro.";
        } else if (msg.includes("network") || msg.includes("timeout")) {
          mensagemErro = "🌐 Erro de conexão. Verifique sua internet e tente novamente.";
        } else if (msg.includes("unexpected_failure")) {
          mensagemErro = "❌ Falha ao enviar email. Tente novamente mais tarde ou entre em contato com suporte.";
        } else {
          mensagemErro = error.message || "Erro ao enviar email de recuperação";
        }
      }

      toast.error(mensagemErro);
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="auth-background min-h-screen flex items-center justify-center p-4">
      <div className="auth-container w-full max-w-md">
        <div className="text-center mb-8">
          <img src={logoLoginCadastro} alt="Ateliê Pro" className="auth-logo h-20 mx-auto mb-4" />
          <p className="auth-subtitle text-purple-100">Gestão profissional para ateliês</p>
        </div>

        <Card className="auth-card bg-white shadow-2xl">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold text-purple-900">Entrar</CardTitle>
            <p className="text-purple-600">Acesse sua conta</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-purple-900 font-medium">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="auth-input bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
                  placeholder="seu@email.com"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-purple-900 font-medium">Senha</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="auth-input bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 pr-10"
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>
              <div className="text-right">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-purple-600 hover:text-purple-700 underline"
                >
                  Esqueci minha senha
                </button>
              </div>
              <Button
                type="submit"
                className="auth-button w-full bg-gradient-to-r from-purple-600 via-purple-500 to-pink-500 hover:from-purple-700 hover:via-purple-600 hover:to-pink-600 text-white font-semibold py-3"
                disabled={loading}
              >
                {loading ? "Entrando..." : "Entrar"}
              </Button>
            </form>
            <div className="mt-6 text-center">
              <p className="text-gray-600">
                Não tem uma conta?{" "}
                <Link to="/cadastro" className="auth-link text-purple-600 font-medium">
                  Cadastre-se
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Modal de Recuperação de Senha */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Recuperar Senha</DialogTitle>
            <DialogDescription>
              Digite seu email e enviaremos um link para redefinir sua senha.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="reset-email">Email</Label>
              <Input
                id="reset-email"
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                placeholder="seu@email.com"
                required
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowForgotPassword(false)}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={resetLoading}>
                {resetLoading ? "Enviando..." : "Enviar Email"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
